import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[hasAnyRole]',
  standalone: true
})
export class HasAnyRoleDirective implements OnInit, OnDestroy {
  private roles: string[] = [];
  private subscription?: Subscription;
  private hasView = false;
  private currentUser: any = null;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private authService: AuthService
  ) {}

  @Input() set hasAnyRole(roles: string[]) {
    this.roles = roles || [];
    this.updateView();
  }

  ngOnInit() {
    // Subscribe to user changes to update view when user changes
    this.subscription = this.authService.currentUser$.subscribe((user) => {
      this.currentUser = user;
      this.updateView();
    });
    this.updateView();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private updateView() {
    const hasRole = this.checkRoles();
    
    if (hasRole && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasRole && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }

  private checkRoles(): boolean {
    if (!this.roles || this.roles.length === 0) {
      return false;
    }

    // Special case: 'user' role means user is NOT CVKD and NOT TinDung
    if (this.roles.length === 1 && this.roles[0] === 'user') {
      const roleStr = localStorage.getItem('role');
      if (roleStr) {
        try {
          const roles = JSON.parse(roleStr);
          if (Array.isArray(roles)) {
            const isCVKD = roles.includes('NHAN_VIEN_BAN_HANG');
            const isTinDung = roles.includes('TIN_DUNG');
            // User role means not CVKD and not TinDung
            return !isCVKD && !isTinDung;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      // If no roles in localStorage, assume user role
      return true;
    }

    // Check localStorage 'role' (array of role strings)
    const roleStr = localStorage.getItem('role');
    if (roleStr) {
      try {
        const roles = JSON.parse(roleStr);
        if (Array.isArray(roles)) {
          const hasAny = this.roles.some(requiredRole => roles.includes(requiredRole));
          if (hasAny) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check localStorage 'jhi-cn_role' (array of role objects with role property)
    const cnRoleStr = localStorage.getItem('jhi-cn_role');
    if (cnRoleStr) {
      try {
        const cnRoles = JSON.parse(cnRoleStr);
        if (Array.isArray(cnRoles)) {
          const hasAny = this.roles.some(requiredRole => 
            cnRoles.some((roleObj: any) => roleObj?.role === requiredRole || roleObj?.role?.includes(requiredRole))
          );
          if (hasAny) return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check currentUser from AuthService
    if (this.currentUser) {
      // Check roles array
      if (this.currentUser.roles && Array.isArray(this.currentUser.roles)) {
        const hasAny = this.roles.some(requiredRole => 
          this.currentUser.roles.some((role: any) => {
            if (typeof role === 'string') {
              return role === requiredRole || role.includes(requiredRole);
            }
            // Support both 'role' and 'name' properties for role objects
            const roleValue = role?.role || role?.name;
            return roleValue === requiredRole || roleValue?.includes(requiredRole);
          })
        );
        if (hasAny) return true;
      }

      // Check global_roles array
      if (this.currentUser.global_roles && Array.isArray(this.currentUser.global_roles)) {
        const hasAny = this.roles.some(requiredRole => 
          this.currentUser.global_roles.some((role: any) => {
            if (typeof role === 'string') {
              return role === requiredRole || role.includes(requiredRole);
            }
            // Support both 'role' and 'name' properties for role objects
            const roleValue = role?.role || role?.name;
            return roleValue === requiredRole || roleValue?.includes(requiredRole);
          })
        );
        if (hasAny) return true;
      }
    }

    return false;
  }
}

